import { signal, type Signal } from '@preact/signals';
import type { AvailabilityState, AiMode } from '../lib/availability.js';

/**
 * Pièce jointe image telle que le widget la manipule : métadonnées + URL
 * absolue vers le backend. JAMAIS d'octets — c'est ce qui rend la
 * persistance localStorage viable (contrat 2.1 : quelques Ko par message).
 */
export type Attachment = {
  id: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
  url: string;
  /** `null` pour une pièce jointe antérieure à la migration 0014 (nom générique côté serveur). */
  filename: string | null;
};

export type Message = {
  id: string;
  role: 'visitor' | 'ai' | 'agent' | 'system';
  content: string;
  createdAt: number;
  /**
   * Quick win §9 : `true` quand l'envoi WS a échoué (socket fermé au moment
   * de `send()`) — le message reste visible (pas de perte silencieuse),
   * marqué "Non envoyé" avec un bouton "Réessayer", et retenté
   * automatiquement à la reconnexion (cf. App.tsx `retryFailedMessages`).
   * Absent/`false` = envoyé normalement.
   */
  failed?: boolean;
  attachments?: Attachment[];
};

/**
 * Clé de réconciliation d'un envoi visiteur : texte + ids de pièces jointes.
 * Le texte seul ne suffit plus depuis le lot 5 — deux messages « photo
 * seule » ont tous les deux un contenu vide et seraient confondus (l'un
 * effacerait l'autre au retour de la resync REST).
 */
export function messageKey(m: Message): string {
  const ids = (m.attachments ?? [])
    .map((a) => a.id)
    .sort()
    .join(',');
  return `${m.content.trim()}|${ids}`;
}

export type PendingSend = { content: string; attachments: { id: string; url: string }[] };

export type Status = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed' | 'closed';
export type EscalationStatus = 'none' | 'pending' | 'agent_joined';

export type { AvailabilityState, AiMode };

export type Store = {
  messages: Signal<Message[]>;
  status: Signal<Status>;
  conversationId: Signal<string | null>;
  aiThinking: Signal<boolean>;
  escalation: Signal<EscalationStatus>;
  isOpen: Signal<boolean>;
  availability: Signal<AvailabilityState>;
  availabilityMessage: Signal<string>;
  aiMode: Signal<AiMode>;
  unreadCount: Signal<number>;
  emailCaptured: Signal<boolean>;
  contactEmail: Signal<string | null>;
  contactName: Signal<string | null>;
  /** Envoi mis en attente tant que l'e-mail de contact n'est pas validé
   * (hors ligne). Depuis le lot 5 il peut ne contenir que des photos. */
  pendingMessage: Signal<PendingSend | null>;
  showSoftInvite: Signal<boolean>;
  // File FIFO des ids `local-*` en attente d'un ack serveur (cf. confirmAck).
  // Pas un signal : pure bookkeeping interne, aucun composant n'en dépend
  // pour son rendu.
  pendingAckQueue: string[];
  /** Erreur d'envoi à afficher au-dessus du composer (ex. photo expirée). */
  sendError: Signal<string | null>;
};

export type CreateStoreOptions = {
  messages?: Message[];
  conversationId?: string | null;
  isOpen?: boolean;
  emailCaptured?: boolean;
  contactEmail?: string | null;
  contactName?: string | null;
};

export function createStore(initial?: CreateStoreOptions): Store {
  return {
    messages: signal<Message[]>(initial?.messages ?? []),
    status: signal<Status>('idle'),
    conversationId: signal<string | null>(initial?.conversationId ?? null),
    aiThinking: signal<boolean>(false),
    escalation: signal<EscalationStatus>('none'),
    isOpen: signal<boolean>(initial?.isOpen ?? false),
    availability: signal<AvailabilityState>('unknown'),
    availabilityMessage: signal<string>(''),
    aiMode: signal<AiMode>('auto'),
    unreadCount: signal<number>(0),
    emailCaptured: signal<boolean>(initial?.emailCaptured ?? false),
    contactEmail: signal<string | null>(initial?.contactEmail ?? null),
    contactName: signal<string | null>(initial?.contactName ?? null),
    pendingMessage: signal<PendingSend | null>(null),
    showSoftInvite: signal<boolean>(false),
    pendingAckQueue: [],
    sendError: signal<string | null>(null),
  };
}

// Déduplique par id : un même message peut arriver deux fois (ré-émission WS
// après reconnexion, chevauchement avec une resync REST) — on ne veut jamais
// de doublon visible, et une version plus tardive du même id (ex. venant du
// serveur, faisant autorité) remplace l'existante à sa place d'origine.
export function appendMessage(store: Store, msg: Message): void {
  const idx = store.messages.value.findIndex((m) => m.id === msg.id);
  if (idx !== -1) {
    const copy = store.messages.value.slice();
    copy[idx] = msg;
    store.messages.value = copy;
    return;
  }
  // Bug prod 30/09 (doublon visiteur) : un message visiteur qui arrive avec
  // un id serveur définitif peut correspondre à un envoi optimiste local pas
  // encore réconcilié par l'ack (ack manqué, ou chemin où l'appelant ne passe
  // pas par confirmAck) — on remplace le local à sa place plutôt que
  // d'ajouter un doublon en fin de liste.
  if (msg.role === 'visitor' && !msg.id.startsWith('local-')) {
    const key = messageKey(msg);
    const localIdx = store.messages.value.findIndex(
      (m) => m.id.startsWith('local-') && m.role === 'visitor' && messageKey(m) === key,
    );
    if (localIdx !== -1) {
      const copy = store.messages.value.slice();
      copy[localIdx] = msg;
      store.messages.value = copy;
      return;
    }
  }
  store.messages.value = [...store.messages.value, msg];
}

// Quick win §9 : marque un message visiteur comme non-envoyé (WS fermé au
// moment de l'appel `send()`) — le message reste dans la liste (pas de perte
// silencieuse), avec un état visuel "Non envoyé" + bouton "Réessayer".
export function markSendFailed(store: Store, localId: string): void {
  const idx = store.messages.value.findIndex((m) => m.id === localId);
  if (idx === -1) return;
  const copy = store.messages.value.slice();
  copy[idx] = { ...copy[idx]!, failed: true };
  store.messages.value = copy;
}

// Réciproque de markSendFailed : appelée quand une relance (manuelle ou
// automatique à la reconnexion) a effectivement été envoyée sur la WS — le
// message repasse en attente d'ack normal (cf. trackLocalSend/confirmAck),
// même id local, donc aucun doublon créé.
export function markSendRetried(store: Store, localId: string): void {
  const idx = store.messages.value.findIndex((m) => m.id === localId);
  if (idx === -1) return;
  const copy = store.messages.value.slice();
  copy[idx] = { ...copy[idx]!, failed: false };
  store.messages.value = copy;
}

// Mémorise l'id local optimiste d'un envoi visiteur, dans l'ordre d'envoi
// (file FIFO) — permet à confirmAck() de savoir quel message local un ack
// serveur confirme, sans dépendre du contenu (deux envois identiques restent
// distingables par leur ordre).
export function trackLocalSend(store: Store, localId: string): void {
  store.pendingAckQueue.push(localId);
}

// Réconciliation par ack (bug prod 30/09) : le serveur n'écho pas les
// messages visiteur sur la WS, il envoie `{ type: 'ack', messageId }` avec
// l'id définitif. On remplace le plus ancien `local-*` en attente par cet id,
// à la même position — le message ne redevient jamais "en attente" après une
// resync REST, qui le verrait sinon comme un doublon plutôt qu'une confirmation
// (cf. mergeHistory, filet de sécurité si l'ack est raté, ex. socket coupé).
export function confirmAck(store: Store, serverId: string): void {
  const localId = store.pendingAckQueue.shift();
  if (!localId) return;
  const idx = store.messages.value.findIndex((m) => m.id === localId);
  if (idx === -1) return;
  const copy = store.messages.value.slice();
  copy[idx] = { ...copy[idx]!, id: serverId };
  store.messages.value = copy;
}

// Fusionne l'historique local (optimiste + reçu par WS) avec l'historique
// faisant autorité renvoyé par l'API REST au (re)branchement — c'est le
// mécanisme de rattrapage quand un WS a été coupé silencieusement (ex. Safari
// iOS qui gèle la connexion en arrière-plan) : les messages envoyés par un
// agent pendant la coupure ont été stockés en base mais jamais reçus par le
// widget, cette fusion les récupère. Les messages `local-*` sont des envois
// visiteur optimistes pas encore confirmés par le serveur : on les garde tels
// quels, à la suite — SAUF réconciliation par contenu (bug prod 30/09) : un
// `local-*` (role visitor) est considéré confirmé, donc supprimé, s'il existe
// un message visitor serveur de même contenu (trim) pas déjà apparié — un
// filet de sécurité pour le cas où l'ack a été raté (confirmAck) ou le
// doublon a déjà été persisté (localStorage) avant ce correctif.
export function mergeHistory(local: Message[], server: Message[]): Message[] {
  const byId = new Map<string, Message>();
  for (const m of local) {
    if (!m.id.startsWith('local-')) byId.set(m.id, m);
  }
  for (const m of server) {
    byId.set(m.id, m);
  }
  const merged = Array.from(byId.values()).sort((a, b) => a.createdAt - b.createdAt);

  const pendingLocal = local.filter((m) => m.id.startsWith('local-'));
  // Appariement un-pour-un, dans l'ordre : deux "test" locaux ↔ deux "test"
  // serveur, sans confondre un local avec un serveur déjà consommé.
  const serverVisitorKeys = server.filter((m) => m.role === 'visitor').map(messageKey);
  const consumed = new Array<boolean>(serverVisitorKeys.length).fill(false);
  const stillPending: Message[] = [];
  for (const m of pendingLocal) {
    if (m.role !== 'visitor') {
      stillPending.push(m);
      continue;
    }
    const key = messageKey(m);
    const idx = serverVisitorKeys.findIndex((c, i) => !consumed[i] && c === key);
    if (idx !== -1) {
      consumed[idx] = true; // confirmé par le serveur — déjà présent dans `merged`
    } else {
      stillPending.push(m);
    }
  }

  return [...merged, ...stillPending];
}

/**
 * Retire le dernier envoi optimiste porteur de pièces jointes. Appelé quand
 * le serveur refuse la liaison (photo déjà envoyée depuis un autre onglet, ou
 * purgée après 1 h sans envoi) : sans ça, la bulle resterait affichée
 * indéfiniment alors qu'aucun message n'a été créé côté serveur, et la
 * resync REST ne la ferait jamais disparaître.
 */
export function dropPendingAttachmentSend(store: Store): boolean {
  const msgs = store.messages.value;
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i]!;
    if (m.id.startsWith('local-') && m.role === 'visitor' && (m.attachments ?? []).length > 0) {
      store.messages.value = [...msgs.slice(0, i), ...msgs.slice(i + 1)];
      const queueIdx = store.pendingAckQueue.indexOf(m.id);
      if (queueIdx !== -1) store.pendingAckQueue.splice(queueIdx, 1);
      return true;
    }
  }
  return false;
}

export function setStatus(store: Store, status: Status): void {
  store.status.value = status;
}

export function setConversationId(store: Store, id: string | null): void {
  store.conversationId.value = id;
}

// La bulle « … » ne doit apparaître qu'en mode IA active (aiMode === 'auto').
// En mode humain (aiMode === 'off', cas de la prod sans IA), aucune réponse
// IA n'arrivera jamais pour la faire disparaître : elle resterait affichée
// indéfiniment. Voir App.tsx pour le timeout de sécurité (60s) et la remise
// à false dès qu'un message d'agent humain arrive.
export function startAiThinking(store: Store): void {
  if (store.aiMode.value === 'auto') {
    store.aiThinking.value = true;
  }
}

export function stopAiThinking(store: Store): void {
  if (store.aiThinking.value) {
    store.aiThinking.value = false;
  }
}
